import { DateTimeFormatter, ZoneId } from '@js-joda/core';
import type { Instant, LocalDate } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import type {
    TradingHoursIngestionCounts,
    TradingHoursIngestionView,
} from '@thinkalpha/quant-ws-client/contracts/trading-hours-admin.js';

const INSTANT_FORMATTER = DateTimeFormatter.ofPattern('MMM d, yyyy HH:mm:ss').withLocale(Locale.US);
const DATE_FORMATTER = DateTimeFormatter.ofPattern('MMM d, yyyy').withLocale(Locale.US);

export const formatInstant = (instant: Instant | undefined | null): string =>
    instant ? instant.atZone(ZoneId.systemDefault()).format(INSTANT_FORMATTER) : '—';

export const formatLocalDate = (date: LocalDate | undefined | null): string =>
    date ? date.format(DATE_FORMATTER) : '—';

/** The first block of a UUID: enough to tell versions apart at a glance. */
export const shortId = (id: string): string => id.split('-')[0] ?? id;

/** Whole-unit age: "3h", "2d", "45m". */
export const formatAge = (seconds: number | undefined): string => {
    if (seconds === undefined) return '—';
    if (seconds < 60) return `${Math.floor(seconds)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
};

export const formatCounts = (counts: TradingHoursIngestionCounts): string =>
    `${counts.markets.toLocaleString()} markets · ${counts.schedules.toLocaleString()} schedules · ${counts.holidays.toLocaleString()} holidays · ${counts.micMappings.toLocaleString()} MIC mappings`;

export const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** A one-line handle for a version in selects and dialogs. */
export const versionLabel = (version: TradingHoursIngestionView): string =>
    `${shortId(version.id)} · vendor ${formatInstant(version.vendorLastUpdated)} · ${version.status}${version.blacklisted ? ' · blacklisted' : ''}${version.pinned ? ' · pinned' : ''}`;

/** Split pasted ids on whitespace and commas, dropping blanks and duplicates. */
export const parseIdsFromText = (text: string): readonly string[] =>
    text
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter((id) => id.length > 0)
        .values()
        .unique()
        .toArray();
